import { Expose, Transform, Type } from 'class-transformer';
import { SafeUrl } from '@angular/platform-browser';
import { PaginatedEntity } from '@core/entities/_extra';
import { SurveyNotificationOption, SurveyOptions, SurveyViewOption } from '@core/enums';
import { BeautifyArrayOfNumbers } from '@core/utils';

export class CrmSurveysListEntity extends PaginatedEntity {
  @Expose({ name: 'survey' })
  surveys: CrmSurveyEntity[];
}

export class CrmSurveyEntity {
  totalCount: number;
  id: number;
  @Expose({ name: 'author_id' })
  authorId: number;
  @Expose({ name: 'author' })
  authorEmail: string;

  picture: string; // long string as base64

  title: string;
  description: string;

  @Transform(({ value }) => parseInt(value, 10))
  votingOption: SurveyOptions;

  @Expose({ name: 'survey_type' })
  surveyType: string;

  @Expose({ name: 'survey_view_option' })
  @Transform(({ value }) => parseInt(value, 10))
  surveyViewOption: SurveyViewOption;

  @Expose({ name: 'additional_anonymous_voting' })
  @Transform(({ value }) => value === 'true')
  additionalAnonymousVoting: boolean;

  @Expose({ name: 'additional_cast_vote' })
  @Transform(({ value }) => value === 'true')
  additionalCastVote: boolean;

  @Expose({ name: 'survey_notification_option' })
  @Transform((item) => (item.value ? BeautifyArrayOfNumbers(item.value) : null))
  surveyNotificationOption: SurveyNotificationOption[];

  status: number;

  @Expose({ name: 'survey_start_date' })
  surveyStartDate: string;

  @Expose({ name: 'survey_end_date' })
  surveyEndDate: string;

  @Expose({ name: 'created_at' })
  createdAt: string;

  @Expose({ name: 'surveyAnswerOption' })
  @Type(() => CrmSurveyAnswerOptionEntity)
  surveyAnswerOptions: CrmSurveyAnswerOptionEntity[];

  @Type(() => CrmSurveyVoteEntity)
  votes: CrmSurveyVoteEntity[];

  @Expose({ name: 'TotalVoteCount' })
  totalVoteCount: number;

  // For UI purposes
  imageUrl: SafeUrl | string;

  get diffPercentage(): number {
    const startDate = new Date(this.surveyStartDate);
    const endDate = new Date(this.surveyEndDate);

    const totalDifference = endDate.getTime() - startDate.getTime();

    const remainingDays = endDate.getTime() - new Date().getTime();

    if (remainingDays <= 0) {
      return 100;
    }

    return ((totalDifference - remainingDays) / totalDifference) * 100;
  }

  get color(): string {
    const percentage = this.diffPercentage;

    if (percentage < 40) {
      return 'var(--light-teal)';
    } else if (percentage >= 40 && percentage < 80) {
      return 'var(--dark-yellow)';
    } else {
      return 'var(--light-orange)';
    }
  }
}

export class CrmSurveyAnswerOptionEntity {
  id: number;
  @Expose({ name: 'survey_id' })
  surveyId: number;
  @Expose({ name: 'survey_answer' })
  surveyAnswer: string;
  answerId: number;
  @Type(() => CrmSurveyAnswerVotedUserEntity)
  votedUsers: CrmSurveyAnswerVotedUserEntity[];
  votedUsersCount: number;
}

export class CrmSurveyAnswerVotedUserEntity {
  @Expose({ name: 'user_id' })
  userId: string;

  @Expose({ name: 'tineon_user_id' })
  tineonUserId: number;

  voterName: string;
}

export class CrmSurveyVoteEntity {
  id: number;

  @Expose({ name: 'survey_id' })
  surveyId: number;

  @Expose({ name: 'user_id' })
  userId: string;

  @Expose({ name: 'tineon_user_id' })
  tineonUserId: number;

  @Expose({ name: 'survey_answer_id' })
  @Transform(({ value }) => parseInt(value, 10))
  surveyAnswerId: number;

  @Expose({ name: 'survey_type' })
  surveyType: string;
}
